import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePublicClient } from 'wagmi';
import {
  decodeEventLog,
  formatEther,
  formatUnits,
  type Address,
  type Hash,
  parseAbiItem,
} from 'viem';
import { splitterHistoryEventsAbi } from '../abi/splitterHistoryEvents';
import { erc20Abi } from '../abi/erc20';

const MAX_ROWS = 60;

export type SplitterTxHistoryProps = {
  splitterAddress: Address;
  explorerBaseUrl: string | undefined;
};

type RowKind = 'native_deposit' | 'native_claim' | 'erc20_claim';

type HistoryRow = {
  kind: RowKind;
  blockNumber: bigint;
  logIndex: number;
  txHash: Hash;
  from?: Address;
  to?: Address;
  token?: Address;
  amount: bigint;
  decimals?: number;
  symbol?: string;
};

function txUrl(explorerBaseUrl: string | undefined, hash: Hash): string | undefined {
  if (!explorerBaseUrl) return undefined;
  return `${explorerBaseUrl.replace(/\/$/, '')}/tx/${hash}`;
}

function shortAddr(a: string) {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

async function fetchEvmHistory(
  publicClient: NonNullable<ReturnType<typeof usePublicClient>>,
  splitter: Address
): Promise<HistoryRow[]> {
  const evIn = parseAbiItem('event PaymentReceived(address from, uint256 amount)');
  const evOut = parseAbiItem('event PaymentReleased(address to, uint256 amount)');
  const evTok = parseAbiItem('event ERC20PaymentReleased(address indexed token, address to, uint256 amount)');

  const tryLogs = async (fromBlock: bigint) => {
    const [received, released, erc20] = await Promise.all([
      publicClient.getLogs({
        address: splitter,
        event: evIn,
        fromBlock,
        toBlock: 'latest',
      }),
      publicClient.getLogs({
        address: splitter,
        event: evOut,
        fromBlock,
        toBlock: 'latest',
      }),
      publicClient.getLogs({
        address: splitter,
        event: evTok,
        fromBlock,
        toBlock: 'latest',
      }),
    ]);
    return { received, released, erc20 };
  };

  let received: Awaited<ReturnType<typeof publicClient.getLogs>> = [];
  let released: typeof received = [];
  let erc20: typeof received = [];

  try {
    const r = await tryLogs(BigInt(0));
    received = r.received;
    released = r.released;
    erc20 = r.erc20;
  } catch {
    const latest = await publicClient.getBlockNumber();
    const span = BigInt(49_000);
    const fromBlock = latest > span ? latest - span : BigInt(0);
    const r = await tryLogs(fromBlock);
    received = r.received;
    released = r.released;
    erc20 = r.erc20;
  }

  const rows: HistoryRow[] = [];

  for (const log of received) {
    if (log.blockNumber == null || log.transactionHash == null || log.logIndex == null) continue;
    const ev = decodeEventLog({
      abi: splitterHistoryEventsAbi,
      data: log.data,
      topics: log.topics,
    });
    if (ev.eventName !== 'PaymentReceived') continue;
    rows.push({
      kind: 'native_deposit',
      blockNumber: log.blockNumber,
      logIndex: log.logIndex,
      txHash: log.transactionHash,
      from: ev.args.from,
      amount: ev.args.amount,
    });
  }
  for (const log of released) {
    if (log.blockNumber == null || log.transactionHash == null || log.logIndex == null) continue;
    const ev = decodeEventLog({
      abi: splitterHistoryEventsAbi,
      data: log.data,
      topics: log.topics,
    });
    if (ev.eventName !== 'PaymentReleased') continue;
    rows.push({
      kind: 'native_claim',
      blockNumber: log.blockNumber,
      logIndex: log.logIndex,
      txHash: log.transactionHash,
      to: ev.args.to,
      amount: ev.args.amount,
    });
  }

  const tokenMeta = new Map<string, { decimals: number; symbol: string }>();
  for (const log of erc20) {
    if (log.blockNumber == null || log.transactionHash == null || log.logIndex == null) continue;
    const ev = decodeEventLog({
      abi: splitterHistoryEventsAbi,
      data: log.data,
      topics: log.topics,
    });
    if (ev.eventName !== 'ERC20PaymentReleased') continue;
    const token = ev.args.token;
    const key = token.toLowerCase();
    if (!tokenMeta.has(key)) {
      try {
        const [decimals, symbol] = await Promise.all([
          publicClient.readContract({ address: token, abi: erc20Abi, functionName: 'decimals' }),
          publicClient.readContract({ address: token, abi: erc20Abi, functionName: 'symbol' }),
        ]);
        tokenMeta.set(key, { decimals: Number(decimals), symbol: String(symbol) });
      } catch {
        tokenMeta.set(key, { decimals: 18, symbol: 'ERC20' });
      }
    }
    const meta = tokenMeta.get(key)!;
    rows.push({
      kind: 'erc20_claim',
      blockNumber: log.blockNumber,
      logIndex: log.logIndex,
      txHash: log.transactionHash,
      to: ev.args.to,
      token,
      amount: ev.args.amount,
      decimals: meta.decimals,
      symbol: meta.symbol,
    });
  }

  rows.sort((a, b) => {
    if (a.blockNumber !== b.blockNumber) return a.blockNumber < b.blockNumber ? 1 : -1;
    return b.logIndex - a.logIndex;
  });

  return rows.slice(0, MAX_ROWS);
}

export function SplitterTxHistory({ splitterAddress, explorerBaseUrl }: SplitterTxHistoryProps) {
  const publicClient = usePublicClient();

  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['splitter-tx-history', publicClient?.chain?.id, splitterAddress],
    queryFn: () => {
      if (!publicClient) throw new Error('No RPC client');
      return fetchEvmHistory(publicClient, splitterAddress);
    },
    enabled: Boolean(publicClient),
    staleTime: 45_000,
  });

  const label = useMemo(() => {
    if (!data?.length) return '';
    const dep = data.filter((r) => r.kind === 'native_deposit').length;
    const nc = data.filter((r) => r.kind === 'native_claim').length;
    const ec = data.filter((r) => r.kind === 'erc20_claim').length;
    return `${dep} deposit(s), ${nc} native claim(s), ${ec} token claim(s)`;
  }, [data]);

  return (
    <details className="collapse collapse-arrow bg-base-100/40 rounded-box border border-neutral-700/50">
      <summary className="collapse-title text-sm font-medium py-2 min-h-0">
        Activity (deposits &amp; claims)
        {data && data.length > 0 && (
          <span className="block text-xs font-normal opacity-70 mt-0.5">{label}</span>
        )}
      </summary>
      <div className="collapse-content pt-0">
        <p className="text-xs opacity-70 mb-2 leading-relaxed">
          Native <strong>deposits</strong> emit <code className="text-[10px]">PaymentReceived</code>.{' '}
          <strong>Claims</strong> emit <code className="text-[10px]">PaymentReleased</code> (native) or{' '}
          <code className="text-[10px]">ERC20PaymentReleased</code> (tokens). ERC-20 transfers into the contract do
          not emit splitter events—only claims appear here for tokens.
        </p>
        <div className="flex flex-wrap gap-2 mb-2">
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => refetch()} disabled={isLoading}>
            {isLoading ? 'Loading…' : 'Refresh'}
          </button>
          {dataUpdatedAt > 0 && (
            <span className="text-[10px] opacity-60 self-center">
              Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
            </span>
          )}
        </div>

        {isLoading && <p className="text-xs opacity-70">Loading on-chain logs…</p>}
        {isError && (
          <p className="text-xs text-warning">
            Could not load full history (RPC may limit log ranges). Try Refresh or check the contract on the explorer.{' '}
            {error instanceof Error ? error.message : ''}
          </p>
        )}
        {!isLoading && !isError && data && data.length === 0 && (
          <p className="text-xs opacity-70">No matching events yet for this contract.</p>
        )}

        {data && data.length > 0 && (
          <div className="overflow-x-auto max-h-72 overflow-y-auto rounded border border-neutral-700/40">
            <table className="table table-xs table-pin-rows">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Detail</th>
                  <th>Tx</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => {
                  const href = txUrl(explorerBaseUrl, row.txHash);
                  let typeLabel = '';
                  let amountStr = '';
                  let detail = '';
                  if (row.kind === 'native_deposit') {
                    typeLabel = 'Native deposit';
                    amountStr = `${formatEther(row.amount)} (native)`;
                    detail = row.from ? `from ${shortAddr(row.from)}` : '';
                  } else if (row.kind === 'native_claim') {
                    typeLabel = 'Native claim';
                    amountStr = `${formatEther(row.amount)} (to payee)`;
                    detail = row.to ? `to ${shortAddr(row.to)}` : '';
                  } else {
                    typeLabel = 'Token claim';
                    const d = row.decimals ?? 18;
                    amountStr = `${formatUnits(row.amount, d)} ${row.symbol ?? 'token'}`;
                    detail = `${row.to ? `to ${shortAddr(row.to)} · ` : ''}${row.token ? shortAddr(row.token) : ''}`;
                  }
                  return (
                    <tr key={`${row.txHash}-${row.logIndex}`}>
                      <td className="whitespace-nowrap">{typeLabel}</td>
                      <td className="font-mono text-[11px] whitespace-nowrap">{amountStr}</td>
                      <td className="font-mono text-[11px]">{detail}</td>
                      <td>
                        {href ? (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="link link-primary text-[11px]">
                            View
                          </a>
                        ) : (
                          <span className="font-mono text-[10px] opacity-70">{shortAddr(row.txHash)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </details>
  );
}
